import { useState, useRef, useCallback, useMemo } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { getFollowUpQuestions } from '../../../services/UnlimitedService';
import { cleanBookTitle, makeBookId } from '../../Homepage/Contexts/DiffitBooksContext';
import { Mixpanel } from '../../../services/Mixpanel';
import { followUpAlignmentContext } from '../../../utils/curriculum';
import { OPEN_ENDED_FINAL_QUESTION } from '../constants';

const buildFinalAnswersFromQA = (qaHistory) => {
  return qaHistory.map((qa) => {
    if (qa.type === 'book_selection') {
      return {
        type: 'book_selection',
        book_id: qa.book_id,
        start_chapter_idx: qa.start_chapter_idx,
        end_chapter_idx: qa.end_chapter_idx,
      };
    }
    return { question: qa.question, answers: qa.answers };
  });
};

/**
 * Manages the follow-up question state machine — navigating between questions,
 * caching answers for back/forward, and delegating to handleCreate when the flow completes.
 *
 * @param {Object} params
 * @param {Object|null} params.selectedBook - Currently selected book.
 * @param {number} params.startChapter - Start chapter index.
 * @param {number} params.endChapter - End chapter index.
 * @param {Object[]} params.languageStandards - Selected language standards.
 * @param {Object[]} params.contentStandards - Selected content standards.
 * @param {Object|null} [params.curriculumSelection] - Align → Curriculum selection, so
 *   later follow-up questions know the curriculum lesson too.
 * @param {Object[]} params.imageAnalyses - Image analysis results.
 * @param {any} params.sourceContent - Extracted source content.
 * @param {Function} params.setSourceContent - Setter for source content.
 * @param {Function} params.setUploadedPdfURL - Setter for uploaded PDF URL.
 * @param {Function} params.setImageAnalyses - Setter for image analyses.
 * @param {Function} params.getUnlimitedTrackingContext - Returns Mixpanel tracking context.
 * @param {Function} params.handleCreate - Resource creation function from useResourceCreation.
 * @returns {{ showFollowUp, currentQuestion, questionKey, isLoadingNextQuestion, restoredSelections, previousQA, handleFollowUpAnswered, handleBookConfirmed, handleSourceContentProvided, handleFollowUpBack, handleFollowUpSkip, startFollowUpFlow }}
 */
export const useFollowUpFlow = ({
  selectedBook,
  startChapter,
  endChapter,
  languageStandards,
  contentStandards,
  curriculumSelection = null,
  imageAnalyses,
  sourceContent,
  setSourceContent,
  setUploadedPdfURL,
  setImageAnalyses,
  getUnlimitedTrackingContext,
  handleCreate,
  savedRequest,
  selectedMtssTier = null,
}) => {
  const { anythingElseFollowUp } = useFlags();

  const [showFollowUp, setShowFollowUp] = useState(false);
  const [currentQuestion, _setCurrentQuestion] = useState(null);
  const [questionKey, setQuestionKey] = useState(0);
  const setCurrentQuestion = useCallback((q) => {
    _setCurrentQuestion(q);
    if (q) setQuestionKey((k) => k + 1);
  }, []);
  const [restoredSelections, setRestoredSelections] = useState(null);
  const forwardCache = useRef([]);
  const [previousQA, setPreviousQA] = useState([]);
  const [isLoadingNextQuestion, setIsLoadingNextQuestion] = useState(false);
  const [generationSessionId, setGenerationSessionId] = useState(null);

  const buildSelectedBookParam = useCallback(
    () =>
      selectedBook
        ? {
            book_id: makeBookId(selectedBook),
            title: cleanBookTitle(selectedBook.title),
            author: selectedBook.author,
            start_chapter_idx: startChapter,
            end_chapter_idx: endChapter,
            chapter_names: selectedBook.chapters
              .slice(startChapter, endChapter + 1)
              .map((ch) => (typeof ch === 'object' ? ch.title : ch)),
          }
        : undefined,
    [selectedBook, startChapter, endChapter]
  );

  // Alignment context for every follow-up question after the first; buildContext in
  // useSubmitFlow uses the same helper for the first question.
  const buildAlignmentContext = useCallback(
    () => followUpAlignmentContext({ languageStandards, contentStandards, curriculumSelection }),
    [languageStandards, contentStandards, curriculumSelection]
  );

  const proceedToFinalQuestionOrCreate = useCallback(
    (qaHistory, passedSourceContent = null, alsoShowFollowUp = false) => {
      if (anythingElseFollowUp) {
        setCurrentQuestion(OPEN_ENDED_FINAL_QUESTION);
        if (alsoShowFollowUp) setShowFollowUp(true);
        Mixpanel.track('Unlimited Open-ended Question Shown', {
          question_number: qaHistory.length + 1,
          ...getUnlimitedTrackingContext(),
        });
      } else {
        Mixpanel.track('Unlimited Open-ended Question Skipped', {
          question_number: qaHistory.length + 1,
          ...getUnlimitedTrackingContext(),
        });
        const finalAnswers = buildFinalAnswersFromQA(qaHistory);
        handleCreate(finalAnswers, passedSourceContent, {
          onFinally: () => setShowFollowUp(false),
          generationSessionId,
        });
      }
    },
    [
      anythingElseFollowUp,
      generationSessionId,
      getUnlimitedTrackingContext,
      handleCreate,
      setCurrentQuestion,
    ]
  );

  /**
   * Called by useSubmitValidation after the initial getFollowUpQuestions API call succeeds.
   * Sets up the flow state and shows the first question (or skips to create).
   */
  const startFollowUpFlow = useCallback(
    (result, _request, sessionId) => {
      // Note: savedRequest is set eagerly in useSubmitFlow before the API call,
      // so we don't need to set it here.
      setGenerationSessionId(sessionId);

      if (result.source_content) setSourceContent(result.source_content);
      if (result.image_analyses) setImageAnalyses(result.image_analyses);

      setPreviousQA([]);

      if (!result.done && result.question) {
        setCurrentQuestion(result.question);
        setShowFollowUp(true);
        Mixpanel.track('Unlimited Follow-up Question Shown', {
          question_number: 1,
          question_id: result.question.id,
          ...getUnlimitedTrackingContext(),
        });
      } else {
        proceedToFinalQuestionOrCreate([], result.source_content || null, true);
      }
    },
    [
      setSourceContent,
      setImageAnalyses,
      setCurrentQuestion,
      getUnlimitedTrackingContext,
      proceedToFinalQuestionOrCreate,
    ]
  );

  const handleFollowUpAnswered = useCallback(
    async (answer) => {
      setRestoredSelections(null);
      if (currentQuestion?.type === 'open_ended') {
        let finalQA = previousQA;
        if (answer.answers && answer.answers.length > 0 && answer.answers[0]) {
          finalQA = [
            ...previousQA,
            {
              id: currentQuestion.id,
              question: currentQuestion.question,
              answers: answer.answers,
              questionObj: currentQuestion,
              selectedOptions: answer.selectedOptions,
              otherText: answer.otherText,
            },
          ];
        }

        Mixpanel.track('Unlimited Open-ended Question Answered', {
          had_additional_context: answer.answers && answer.answers.length > 0,
          ...getUnlimitedTrackingContext(),
        });

        const finalAnswers = buildFinalAnswersFromQA(finalQA);
        await handleCreate(finalAnswers, null, {
          onFinally: () => setShowFollowUp(false),
          generationSessionId,
        });
        return;
      }

      const qaEntry = {
        id: currentQuestion.id,
        question: currentQuestion.question,
        answers: answer.answers || [],
        options: currentQuestion.options,
        questionObj: currentQuestion,
        selectedOptions: answer.selectedOptions,
        otherText: answer.otherText,
      };

      const updatedPreviousQA = [...previousQA, qaEntry];
      setPreviousQA(updatedPreviousQA);

      Mixpanel.track('Unlimited Follow-up Question Answered', {
        question_id: currentQuestion.id,
        question_text: currentQuestion.question,
        selected_answers: answer.answers,
        question_number: updatedPreviousQA.length,
        ...getUnlimitedTrackingContext(),
      });

      // Check forward cache for unchanged answers
      if (forwardCache.current.length > 0) {
        const cached = forwardCache.current[forwardCache.current.length - 1];
        const sameOptions =
          JSON.stringify(answer.selectedOptions) === JSON.stringify(cached.selectedOptions);
        const sameOther = (answer.otherText || '') === (cached.otherText || '');
        if (sameOptions && sameOther) {
          forwardCache.current.pop();
          setRestoredSelections(cached.nextQuestionSelections || null);
          setCurrentQuestion(cached.nextQuestion);
          return;
        }
        forwardCache.current = [];
      }

      setIsLoadingNextQuestion(true);

      try {
        const result = await getFollowUpQuestions({
          request: savedRequest,
          previous_qa: updatedPreviousQA,
          source_content: sourceContent,
          image_analyses: imageAnalyses.length > 0 ? imageAnalyses : undefined,
          detected_book_match: undefined,
          selected_book: buildSelectedBookParam(),
          context: buildAlignmentContext(),
          generation_session_id: generationSessionId,
          ...(selectedMtssTier ? { mtss_tier: selectedMtssTier } : {}),
        });

        if (result.done || !result.question) {
          proceedToFinalQuestionOrCreate(updatedPreviousQA);
        } else {
          setCurrentQuestion(result.question);
          Mixpanel.track('Unlimited Follow-up Question Shown', {
            question_number: updatedPreviousQA.length + 1,
            question_id: result.question.id,
            ...getUnlimitedTrackingContext(),
          });
        }
      } catch (err) {
        console.error('Error fetching next question:', err);
        Mixpanel.track('Unlimited Follow-up Error Recovery', {
          error: err.message,
          question_number: updatedPreviousQA.length + 1,
          context: 'follow_up_answered',
          ...getUnlimitedTrackingContext(),
        });
        proceedToFinalQuestionOrCreate(updatedPreviousQA);
      } finally {
        setIsLoadingNextQuestion(false);
      }
    },
    [
      currentQuestion,
      previousQA,
      sourceContent,
      imageAnalyses,
      savedRequest,
      generationSessionId,
      getUnlimitedTrackingContext,
      handleCreate,
      buildSelectedBookParam,
      buildAlignmentContext,
      proceedToFinalQuestionOrCreate,
      setCurrentQuestion,
      selectedMtssTier,
    ]
  );

  const handleBookConfirmed = useCallback(
    async (bookData) => {
      setRestoredSelections(null);
      forwardCache.current = [];
      const qaEntry = {
        id: currentQuestion.id,
        question: currentQuestion.question,
        answers: ['Book confirmed'],
        type: 'book_selection',
        book_id: bookData.book_id,
        start_chapter_idx: bookData.start_chapter_idx,
        end_chapter_idx: bookData.end_chapter_idx,
        questionObj: currentQuestion,
      };

      const updatedPreviousQA = [...previousQA, qaEntry];
      setPreviousQA(updatedPreviousQA);

      Mixpanel.track('Unlimited Book Confirmed in Follow-up', {
        book_id: bookData.book_id,
        start_chapter: bookData.start_chapter_idx,
        end_chapter: bookData.end_chapter_idx,
        ...getUnlimitedTrackingContext(),
      });

      setIsLoadingNextQuestion(true);

      try {
        const result = await getFollowUpQuestions({
          request: savedRequest,
          previous_qa: updatedPreviousQA,
          source_content: sourceContent,
          context: buildAlignmentContext(),
          generation_session_id: generationSessionId,
          ...(selectedMtssTier ? { mtss_tier: selectedMtssTier } : {}),
        });

        if (result.done || !result.question) {
          proceedToFinalQuestionOrCreate(updatedPreviousQA);
        } else {
          setCurrentQuestion(result.question);
        }
      } catch (err) {
        console.error('Error fetching next question after book confirmation:', err);
        Mixpanel.track('Unlimited Follow-up Error Recovery', {
          error: err.message,
          question_number: updatedPreviousQA.length + 1,
          context: 'book_confirmed',
          ...getUnlimitedTrackingContext(),
        });
        proceedToFinalQuestionOrCreate(updatedPreviousQA);
      } finally {
        setIsLoadingNextQuestion(false);
      }
    },
    [
      currentQuestion,
      previousQA,
      sourceContent,
      savedRequest,
      generationSessionId,
      getUnlimitedTrackingContext,
      buildAlignmentContext,
      proceedToFinalQuestionOrCreate,
      setCurrentQuestion,
      selectedMtssTier,
    ]
  );

  const handleSourceContentProvided = useCallback(
    async (sourceData) => {
      setRestoredSelections(null);
      forwardCache.current = [];

      Mixpanel.track('Unlimited Source Content Provided in Follow-up', {
        has_pdf: !!sourceData.pdfUrl,
        has_text: !!sourceData.text,
        has_urls: sourceData.detectedUrls?.length > 0,
        has_images: sourceData.imageUrls?.length > 0,
        image_count: sourceData.imageUrls?.length || 0,
        ...getUnlimitedTrackingContext(),
      });

      let updatedSourceContent = { ...(sourceContent || {}) };
      if (sourceData.pdfUrl) {
        setUploadedPdfURL(sourceData.pdfUrl);
        updatedSourceContent.files = [{ [sourceData.pdfUrl]: '' }];
      }
      if (sourceData.text) {
        updatedSourceContent.texts = [sourceData.text];
      }
      setSourceContent(updatedSourceContent);

      const qaEntry = {
        id: currentQuestion.id,
        question: currentQuestion.question,
        answers: ['Source content provided'],
        type: 'source_content_provided',
        questionObj: currentQuestion,
      };

      const updatedPreviousQA = [...previousQA, qaEntry];
      setPreviousQA(updatedPreviousQA);

      setIsLoadingNextQuestion(true);

      try {
        const result = await getFollowUpQuestions({
          request: savedRequest,
          previous_qa: updatedPreviousQA,
          uploaded_pdf_url: sourceData.pdfUrl || undefined,
          uploaded_image_urls: sourceData.imageUrls?.length > 0 ? sourceData.imageUrls : undefined,
          urls: sourceData.detectedUrls?.length > 0 ? sourceData.detectedUrls : undefined,
          source_content:
            Object.keys(updatedSourceContent).length > 0 ? updatedSourceContent : undefined,
          selected_book: buildSelectedBookParam(),
          context: buildAlignmentContext(),
          generation_session_id: generationSessionId,
          ...(selectedMtssTier ? { mtss_tier: selectedMtssTier } : {}),
        });

        if (result.source_content) setSourceContent(result.source_content);
        if (result.image_analyses) setImageAnalyses((prev) => [...prev, ...result.image_analyses]);

        if (result.done || !result.question) {
          proceedToFinalQuestionOrCreate(updatedPreviousQA, result.source_content);
        } else {
          setCurrentQuestion(result.question);
          Mixpanel.track('Unlimited Follow-up Question Shown', {
            question_number: updatedPreviousQA.length + 1,
            question_id: result.question.id,
            ...getUnlimitedTrackingContext(),
          });
        }
      } catch (err) {
        console.error('Error fetching next question after source content provided:', err);
        Mixpanel.track('Unlimited Follow-up Error Recovery', {
          error: err.message,
          question_number: updatedPreviousQA.length + 1,
          context: 'source_content_provided',
          ...getUnlimitedTrackingContext(),
        });
        proceedToFinalQuestionOrCreate(updatedPreviousQA, updatedSourceContent);
      } finally {
        setIsLoadingNextQuestion(false);
      }
    },
    [
      currentQuestion,
      previousQA,
      sourceContent,
      savedRequest,
      generationSessionId,
      getUnlimitedTrackingContext,
      setSourceContent,
      setUploadedPdfURL,
      setImageAnalyses,
      buildSelectedBookParam,
      buildAlignmentContext,
      proceedToFinalQuestionOrCreate,
      setCurrentQuestion,
      selectedMtssTier,
    ]
  );

  const handleFollowUpBack = useCallback(() => {
    if (previousQA.length > 0) {
      const lastEntry = previousQA[previousQA.length - 1];
      setPreviousQA(previousQA.slice(0, -1));
      forwardCache.current.push({
        nextQuestion: currentQuestion,
        nextQuestionSelections: restoredSelections,
        selectedOptions: lastEntry.selectedOptions || [],
        otherText: lastEntry.otherText || '',
      });
      setRestoredSelections({
        selectedOptions: lastEntry.selectedOptions || [],
        otherText: lastEntry.otherText || '',
      });
      setCurrentQuestion(lastEntry.questionObj);
    } else {
      forwardCache.current = [];
      setShowFollowUp(false);
      setCurrentQuestion(null);
    }
  }, [previousQA, currentQuestion, restoredSelections, setCurrentQuestion]);

  const handleFollowUpSkip = useCallback(() => {
    forwardCache.current = [];
    Mixpanel.track('Unlimited Follow-up Skipped', {
      skipped_at_question: previousQA.length + 1,
      answers_provided: previousQA.length,
      ...getUnlimitedTrackingContext(),
    });
    const finalAnswers = buildFinalAnswersFromQA(previousQA);
    // A skipped format question is sent with no answers: the backend reads that as a skip.
    if (currentQuestion?.id === 'format') {
      finalAnswers.push({ question: currentQuestion.question, answers: [] });
    }
    handleCreate(finalAnswers, null, {
      onFinally: () => setShowFollowUp(false),
      generationSessionId,
    });
  }, [previousQA, currentQuestion, generationSessionId, getUnlimitedTrackingContext, handleCreate]);

  return useMemo(
    () => ({
      showFollowUp,
      currentQuestion,
      questionKey,
      isLoadingNextQuestion,
      restoredSelections,
      previousQA,
      savedRequest,
      startFollowUpFlow,
      handleFollowUpAnswered,
      handleBookConfirmed,
      handleSourceContentProvided,
      handleFollowUpBack,
      handleFollowUpSkip,
    }),
    [
      showFollowUp,
      currentQuestion,
      questionKey,
      isLoadingNextQuestion,
      restoredSelections,
      previousQA,
      savedRequest,
      startFollowUpFlow,
      handleFollowUpAnswered,
      handleBookConfirmed,
      handleSourceContentProvided,
      handleFollowUpBack,
      handleFollowUpSkip,
    ]
  );
};
